import useSWR from 'swr'
import SpektrixFetcher from '@lib/spektrix/fetcher'
import Fetch from '@lib/fetch'
import { useMemo } from 'react'

// Global cache for plan validation results
const planValidationCache = new Map()

export function useTicketTypes (spektrixId, commissionValue = 0, hideOnlineAreas = false) {
  const { data: tickets } = useSWR(
    spektrixId ? `/instances/${spektrixId}/price-list?expand=2` : null,
    SpektrixFetcher,
    {
      // Add SWR configuration to prevent constant refetching
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 0, // Don't auto-refresh
      dedupingInterval: 60000 // Dedupe requests for 60 seconds
    }
  )

  // Extract unique plan IDs from ticket prices
  const planIds = useMemo(() => {
    if (!tickets?.prices) return []
    return [...new Set(tickets.prices
      .filter(price => price.plan)
      .map(price => price.plan.id || price.plan)
    )]
  }, [tickets])

  // Create a cache key for this specific set of plan IDs
  const cacheKey = planIds.length > 0 ? planIds.sort().join(',') : null

  // Use SWR to fetch and cache plan validation results
  const { data: validationData } = useSWR(
    cacheKey ? `/plans/validate/${cacheKey}` : null,
    async () => {
      // Check if we already have this in our static cache
      if (planValidationCache.has(cacheKey)) {
        return planValidationCache.get(cacheKey)
      }

      // Fetch validation data
      const response = await Fetch('/plans/validate', {
        method: 'POST',
        body: JSON.stringify({ plan_ids: planIds })
      })
      
      if (!response.ok) {
        throw new Error('Failed to validate plans')
      }
      
      const data = await response.json()
      const validPlans = data.valid_plans || {}
      
      // Store in static cache
      planValidationCache.set(cacheKey, validPlans)
      
      return validPlans
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 0, // Plans don't change often
      dedupingInterval: 300000 // Cache for 5 minutes
    }
  )

  const ticketOptions = tickets?.prices
    ?.filter(ticket => {
      // Filter out tickets with band name "online" if hideOnlineAreas is true
      if (hideOnlineAreas && ticket.priceBand?.name?.toLowerCase() === 'online') {
        return false
      }
      
      // Filter out tickets for plans that don't exist in the system
      if (ticket.plan && validationData) {
        const planId = ticket.plan.id || ticket.plan
        if (validationData[planId] === false) {
          return false
        }
      }
      
      return true
    })
    ?.map(ticket => {
      const totalPrice = parseFloat(ticket.amount) + parseFloat(commissionValue || 0)
      return {
        label: `${ticket.ticketType.name} - £${totalPrice.toFixed(2)}`,
        value: ticket.ticketType.id,
        ticket: {
          ...ticket,
          amount: totalPrice,
          originalAmount: ticket.amount
        },
        band: ticket.priceBand
      }
    })

  const returnValue = {
    ticketTypes: tickets?.prices || [],
    ticketOptions: ticketOptions || [],
    isLoading: !tickets || (planIds.length > 0 && !validationData),
    error: tickets?.error,
    plansValidated: !!validationData
  }

  return returnValue
}
